import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import api from "../../../../store/api";
import { AppThunk, RootState } from "../../../../store/app";
import Config from "../../../../config/Config";
import { apiUrls } from "./apiUrls";

interface InitialState {
  isLoading: boolean;
  agGrid: any;
  rowData: any;
  perPage: any;
  details: any;
  fileRowdata: any;
  assessmentResultDetails: any;
  interestAssessmentDetail: any;
  skillAssessmentDetail: any;
  assessmentResultId: any;
  changePasswordDetails: any,
  allExamDetails: any,

}

const initialState = {
  isLoading: false,
  agGrid: null,
  rowData: null,
  perPage: Config.grid.server.gridOptions?.paginationPageSize,
  details: null,
  fileRowdata: null,
  assessmentResultDetails: null,
  interestAssessmentDetail: null,
  skillAssessmentDetail: null,
  assessmentResultId: null,
  changePasswordDetails: null,
  allExamDetails: null

} as InitialState;

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    start: (state) => {
      state.isLoading = true;
    },
    success: (state) => {
      state.isLoading = false;
    },
    failure: (state) => {
      state.isLoading = false;
    },
    setGrid: (state, action: PayloadAction<any>) => {
      state.agGrid = action?.payload;
    },
    setRowData: (state, action: PayloadAction<any>) => {
      state.rowData = action?.payload;
    },
    setFileRowData: (state, action: PayloadAction<any>) => {
      state.fileRowdata = action?.payload;
    },
    setPerPage: (state, action: PayloadAction<any>) => {
      state.perPage = action?.payload;
      if (state.agGrid) {
        state.agGrid.api.paginationSetPageSize(Number(state.perPage));
        state.agGrid.api.setCacheBlockSize(state.perPage);
      }
    },
    setDetails: (state, action: PayloadAction<any>) => {
      state.details = action?.payload;
    },
    setAssessmentResultDetails: (state, action: PayloadAction<any>) => {
      state.assessmentResultDetails = action?.payload;
    },
    setInterestAssessmentDetail: (state, action: PayloadAction<any>) => {
      state.interestAssessmentDetail = action?.payload;
    },
    setSkillAssessmentDetail: (state, action: PayloadAction<any>) => {
      state.skillAssessmentDetail = action?.payload;
    },

    setAssessmentResultId: (state, action: PayloadAction<any>) => {
      state.assessmentResultId = action?.payload;
    },
    setChangePasswordDetails: (state, action: PayloadAction<any>) => {
      state.changePasswordDetails = action.payload;
    },
    setAllExamDetails: (state, action: PayloadAction<any>) => {
      state.allExamDetails = action.payload;
    },
  },
});

export const {
  start,
  success,
  failure,
  setGrid,
  setPerPage,
  setRowData,
  setDetails,
  setAssessmentResultDetails,
  setInterestAssessmentDetail,
  setSkillAssessmentDetail,
  setAssessmentResultId,
  setFileRowData,
  setChangePasswordDetails,
  setAllExamDetails

} = userSlice.actions;

/** For Listing:Start */
export const fetchList =
  (action?: any): AppThunk<any> =>
    async (dispatch, getState: () => RootState) => {
      const initialAppOption: any = getState().auth.initialData;
      try {
        dispatch(start());
        dispatch(setRowData([]));
        const response = await api.post(apiUrls.list, action);
        const modifiedData = response.data.rows.map((row: any) => {
          return {
            ...row,
            disabilities: row.role_id == '2' ? initialAppOption?.disability_status?.find(
              (answer: any) => answer.id == row.disabilities
            )?.name : null,
            household_income: row.role_id == '2' ? initialAppOption?.annual_income?.find(
              (answer: any) => answer.id == row.household_income
            )?.name : null,
            campaign_name: row.role_id == '2' ? row.campaign_name : null,
            dob: row.role_id == '2' ? row.dob : null,
            gender: row.role_id == '2' ? row.gender : null,
            ethnicity: row.role_id == '2' ? row.ethnicity : null,
            veteran: row.role_id == '2' ? row.veteran : null,
            location: row.role_id == '2' ? row.location : null,
            exam_status_display: row.role_id == '2' ? row.exam_status_display : null,
            subscribed: row.role_id == '2' ? row.subscribed : null,
            purchased: row.role_id == '2' ? row.purchased : null,
          };
        });
        dispatch(setRowData(modifiedData));
        dispatch(success());
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure());
        return Promise.reject(error.data);
      }
    };

/** For Listing:End */

export const createRecord =
  (action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(apiUrls.create, action);
        dispatch(success(response.data));
        dispatch(fetchList());
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure(error.data));
        return Promise.reject(error.data);
      }
    };

export const updateRecord =
  (id: number, action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(apiUrls.update(id), action);
        dispatch(success(response.data));
        dispatch(fetchList());
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure(error.data));
        return Promise.reject(error.data);
      }
    };

export const deleteRecord =
  (id: number, action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.delete(apiUrls.delete(id), action);
        dispatch(success(response.data));
        dispatch(fetchList());
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure(error.data));
        return Promise.reject(error.data);
      }
    };

export const details =
  (id: number): AppThunk<any> =>
    async (dispatch) => {
      try {
        const response = await api.get(apiUrls.details(id));
        dispatch(setDetails(response.data.data));
        return Promise.resolve(response.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };

// Api calls for assessment result details: start
export const fetchAssessmentResultDetails =
  (exam_id: number): AppThunk<any> =>
    async (dispatch) => {
      try {
        const response = await api.get(apiUrls.assessmentResultsDetails(exam_id));
        dispatch(setAssessmentResultDetails(response.data.data));
        return Promise.resolve(response.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };
export const fetchInterestAssessmentDetails =
  (exam_id: number): AppThunk<any> =>
    async (dispatch) => {
      try {
        const response = await api.get(apiUrls.interestAssessmentDetails(exam_id));
        dispatch(setInterestAssessmentDetail(response.data.data));
        return Promise.resolve(response.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };

export const fetchSkillAssessmentDetails =
  (exam_id: number): AppThunk<any> =>
    async (dispatch) => {
      try {
        const response = await api.get(apiUrls.skillAssessmentDetails(exam_id));
        dispatch(setSkillAssessmentDetail(response.data.data));
        return Promise.resolve(response.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };

export const fetchPdfAssessmentResult =
  (exam_id: number,requestData:any): AppThunk<any> =>
    async () => {
      try {
        const response = await api.post(apiUrls.pdfResultDetails(exam_id), requestData);
        return Promise.resolve(response.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };

export const fetchEducationLevelsList =
  (): AppThunk<any> =>
    async (dispatch) => {
      try {
        const response = await api.get(apiUrls.getEducationLevels);
        return Promise.resolve(response.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };


export const resetsUserExam =
  (user_id: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        const response = await api.get(apiUrls.USER_RESET_EXAM(user_id));
        return Promise.resolve(response.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };

// Api calls for assessment result details: end

export const changeStatus =
  (id: number, status: number): AppThunk<any> =>
    async (dispatch) => {
      try {
        const response = await api.post(apiUrls.changeStatus(id), { status });
        dispatch(fetchList());
        return Promise.resolve(response.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };



export const inviteWebUser =
  (action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(apiUrls.invite, action);
        dispatch(success(response.data));
        dispatch(fetchList());
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure(error.data));
        return Promise.reject(error.data);
      }
    };


export const importFile =
  (action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(apiUrls.importfile, action);
        // dispatch(fetchList());
        dispatch(success(response.data));
        dispatch(setFileRowData(response.data.import_data));
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure(error.data));
        return Promise.reject(error.data);
      }
    };
export const verifyFile =
  (action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(apiUrls.verifyfile, action);
        // dispatch(fetchList());
        dispatch(success(response.data));
        dispatch(setFileRowData(response.data.import_data));
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure(error.data));
        return Promise.reject(error.data);
      }
    };

export const ChangeAdminPassword =
  (action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        const response = await api.post(apiUrls.adminUserChangePassword, action);
        return Promise.resolve(response.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };
/** For Import:End */



export const setUserSpecialFlag =
  (requestData: any): AppThunk<any> =>
    async () => {
      try {
        const response = await api.post(
          apiUrls.SetUserSpecialFlag,
          requestData
        );
        return Promise.resolve(response.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };



export const fetchAllIndustryExamDetails =
  (id: number): AppThunk<any> =>
    async (dispatch) => {
      try {
        const response = await api.get(apiUrls.getUserIndustyExam(id));
        dispatch(setAllExamDetails(response.data.rows));
        return Promise.resolve(response.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };


const userReducer = userSlice.reducer;
export default userReducer;
